import "./style.css";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";

import { useMessageNotifications } from "../../../context/MessageNotificationContext";
import { getCustomLists, createCustomList } from "../../../functions/customLists";
import { uploadImages } from "../../../functions/uploadImages";
import dataURItoBlob from "../../../helpers/dataURItoBlob";

// Simplified Groups List Component
// ...existing code...
function SimplifiedGroupsList() {
  const { user } = useSelector((state) => ({ ...state }));
  const [ownedGroups, setOwnedGroups] = useState([]);
  const [memberGroups, setMemberGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadGroups = async () => {
      try {
        setLoading(true);
        const response = await getCustomLists(user.token);
        if (response.status === "ok") {
          const userId = (user && (user.id || user._id)) ? (user.id || user._id).toString() : null;

          const normalizeGroup = (group, fallbackIndex = 0, type = 'group') => {
            if (!group) return null;
            const rawOwnerId = group.owner && typeof group.owner === 'object' ? group.owner._id : group.owner;
            const ownerId = rawOwnerId ? rawOwnerId.toString() : null;
            return {
              ...group,
              id: (group.id || group._id || group.listId || `${type}-${fallbackIndex}`),
              owner: group.owner && typeof group.owner === 'object' ? group.owner : (ownerId ? { _id: ownerId } : null),
              ownerId,
            };
          };

          const ownedListsRaw = response.data.ownedLists || [];
          const memberListsRaw = response.data.memberLists || [];

          const ownedNormalized = ownedListsRaw
            .map((group, idx) => normalizeGroup(group, idx, 'owned'))
            .filter(Boolean);

          const memberNormalized = memberListsRaw
            .map((group, idx) => normalizeGroup(group, idx, 'member'))
            .filter(Boolean);

          setOwnedGroups(ownedNormalized);

          const memberOnly = memberNormalized.filter((group) => {
            if (!userId) return true;
            const ownerId = group.ownerId || (group.owner && group.owner._id ? group.owner._id.toString() : null);
            return !ownerId || ownerId !== userId;
          });
          setMemberGroups(memberOnly);
        }
      } catch (error) {
        console.error("Error loading groups:", error);
      } finally {
        setLoading(false);
      }
    };

    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) {
    return (
      <div className="groups_loading">
        <div className="loading_spinner"></div>
        <span>Loading groups...</span>
      </div>
    );
  }

  // Compute unique member count including members, admins and owner
  const computeMembersCount = (group) => {
    const ids = new Set();
    const getId = (item) => {
      if (!item) return null;
      if (typeof item === 'string' || typeof item === 'number') return item.toString();
      if (typeof item === 'object') {
        if (item._id) return item._id.toString();
        if (item.id) return item.id.toString();
      }
      return null;
    };

    (group.members || []).forEach((m) => {
      const id = getId(m);
      if (id) ids.add(id);
    });

    const ownerId = getId(group.owner);
    if (ownerId) ids.add(ownerId);

    if (Array.isArray(group.admins)) {
      group.admins.forEach((a) => {
        const aid = getId(a);
        if (aid) ids.add(aid);
      });
    }

    return ids.size;
  };

  if (ownedGroups.length === 0 && memberGroups.length === 0) {
    return (
      <div className="no_groups">
        <p>No groups yet</p>
        <Link to="/friends/custom-lists" className="create_group_link">
          Create group
        </Link>
      </div>
    );
  }

  return (
    <div className="simplified_groups_sections">
      {/* Your Groups Section */}
      <div className="groups_section">
        <h4 className="section_title">Your Groups ({ownedGroups.length})</h4>
        <div className="simplified_groups_list">
          {ownedGroups.map((group) => (
            <Link
              key={group.id}
              to={`/friends/custom-lists`}
              className="simplified_group_item"
            >
              <div className="group_avatar">
                <img 
                  src={group.groupPicture || '/images/default_group.svg'} 
                  alt={group.name} 
                />
              </div>
              <div className="group_info">
                <span className="group_name">{group.name}</span>
                <span className="group_members_count">
                  {computeMembersCount(group)} members
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
      {/* Groups You're Part Of Section */}
      {memberGroups.length > 0 && (
        <div className="groups_section">
          <h4 className="section_title">Groups You're Part Of ({memberGroups.length})</h4>
          <div className="simplified_groups_list">
            {memberGroups.map((group) => (
              <Link
                key={group.id}
                to={`/friends/custom-lists`}
                className="simplified_group_item"
              >
                <div className="group_avatar">
                  <img 
                    src={group.groupPicture || '/images/default_group.svg'} 
                    alt={group.name} 
                  />
                </div>
                <div className="group_info">
                  <span className="group_name">{group.name}</span>
                  <span className="group_members_count">
                    {computeMembersCount(group)} members
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
// ...existing code...

export default function RightHome({ user }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListDescription, setNewListDescription] = useState("");
  const [, setGroupPicture] = useState("");
  const [groupPicturePreview, setGroupPicturePreview] = useState("");
  const [, setUploadingPicture] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const groupPictureInputRef = useRef(null);
  const { unreadMessageCount } = useMessageNotifications();

  const handleCreateList = async () => {
    if (newListName.trim()) {
      try {
        setCreatingList(true);
        setUploadingPicture(true);
        let groupPictureUrl = "";

        // Upload group picture if selected
        if (groupPicturePreview) {
          const img = dataURItoBlob(groupPicturePreview);
          const path = `${user.username}/group_pictures`;
          let formData = new FormData();
          formData.append("path", path);
          formData.append("file", img);
          
          const uploadResponse = await uploadImages(formData, path, user.token);
          if (Array.isArray(uploadResponse) && uploadResponse.length > 0) {
            groupPictureUrl = uploadResponse[0].url;
          } else {
            console.error("Failed to upload group picture");
            setUploadingPicture(false);
            return;
          }
        }

        const response = await createCustomList(
          {
            name: newListName.trim(),
            description: newListDescription.trim(),
            groupPicture: groupPictureUrl,
          },
          user.token
        );

        if (response.status === "ok") {
          // Reset form
          setNewListName("");
          setNewListDescription("");
          setGroupPicture("");
          setGroupPicturePreview("");
          setShowCreateForm(false);
        } else {
          console.error("Failed to create list:", response.message);
        }
      } catch (err) {
        console.error("Failed to create list:", err);
      } finally {
        setCreatingList(false);
        setUploadingPicture(false);
      }
    }
  };

  const handleGroupPictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setGroupPicturePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeGroupPicture = () => {
    setGroupPicture("");
    setGroupPicturePreview("");
    if (groupPictureInputRef.current) {
      groupPictureInputRef.current.value = "";
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showCreateForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCreateForm]);

  return (
    <div className="right_home">
      <div className="right_home_content scrollbar">
        {/* Profile Link Above */}
        <Link to="/profile" className="left_link hover1 profile_link">
          <img src={user?.picture} alt="" />
          <span>{user?.name || `${user?.first_name || ""} ${user?.last_name || ""}`.trim()}</span>
        </Link>

        {/* Messenger Button */}
        <div 
        onClick={() => window.dispatchEvent(new CustomEvent('toggleMessenger'))} 
        className="left_link hover1 messenger_button"
        style={{ cursor: 'pointer' }}
      >
          <div className="messenger_icon_wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#65676b">
              <path d="M12 2C6.477 2 2 6.145 2 11.333c0 2.333.86 4.467 2.267 6.067L3 22l4.6-1.267c1.4.533 2.933.8 4.4.8 5.523 0 10-4.145 10-9.333S17.523 2 12 2zm1.2 12.533L11.667 13l-2.534 1.533L11.667 11 13.2 12.533z"/>
            </svg>
            {unreadMessageCount > 0 && (
              <span className="messenger_badge" title={`${unreadMessageCount} unread message${unreadMessageCount !== 1 ? 's' : ''}`}>
                {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
              </span>
            )}
          </div>
          <span>Messenger</span>
        </div>

        {/* More Button */}
        {/* <div 
          className="left_link hover1 profile_link"
          onClick={() => setShowMore(!showMore)}
          style={{ cursor: 'pointer' }}
        >
          <div className="more_icon">
            <ArrowDown1 />
          </div>
          <span>More</span>
        </div> */}

        {/* More Menu Items */}
        {/* {showMore && (
          <div className="more_menu">
            <Link to="/friends/custom-lists" className="left_link hover1">
              <img src="../../../icons/friends.png" alt="" />
              <span>Groups</span>
            </Link>
            
            {left.map((item, i) => (
              <LeftLink
                key={i}
                img={item.img}
                text={item.text}
                notification={item.notification}
              />
            ))}
          </div>
        )} */}

        <div className="splitter"></div>

        {/* Simplified Groups Section */}
        <div className="simplified_groups_wrapper">
          <div className="groups_header">
            <h3>Groups</h3>
            <div className="groups_header_actions">
              <button 
                className="create_group_btn"
                onClick={() => setShowCreateForm(true)}
                title="Create new group"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
              <Link to="/friends/custom-lists" className="see_all_groups">
                See all
              </Link>
            </div>
          </div>
          
          {/* Create Group Form */}
          {showCreateForm && createPortal(
            <div className="create_group_form_wrapper" onClick={(e) => {
              if (e.target.classList.contains('create_group_form_wrapper')) {
                setShowCreateForm(false);
              }
            }}>
              <div className="create_group_form" onClick={(e) => e.stopPropagation()}>
                <div className="form_header">
                  <h4>Create New Group</h4>
                  <button 
                    className="close_form_btn"
                    onClick={() => setShowCreateForm(false)}
                  >
                    ×
                  </button>
                </div>
                
                <div className="form_content">
                  <div className="input_wrap">
                    <input
                      type="text"
                      placeholder="Group name"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      className="input_field"
                    />
                  </div>
                  
                  <div className="input_wrap">
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={newListDescription}
                      onChange={(e) => setNewListDescription(e.target.value)}
                      className="input_field"
                    />
                  </div>
                  
                  {/* Group Picture Upload */}
                  <div className="group_picture_upload_wrap">
                    <label className="group_picture_label">Group Picture (optional)</label>
                    <div className="group_picture_upload_area">
                      {groupPicturePreview ? (
                        <div className="group_picture_preview">
                          <img 
                            src={groupPicturePreview} 
                            alt="Group preview" 
                            className="group_picture_preview_img"
                          />
                          <button 
                            type="button" 
                            className="remove_picture_btn"
                            onClick={removeGroupPicture}
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div 
                          className="group_picture_upload_placeholder"
                          onClick={() => groupPictureInputRef.current?.click()}
                        >
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <path d="M21 15l-5-5L5 21"/>
                          </svg>
                          <p>Click to upload group picture</p>
                          <span>JPG, PNG, GIF up to 5MB</span>
                        </div>
                      )}
                      <input
                        ref={groupPictureInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleGroupPictureChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>
                  
                  <div className="form_actions">
                    <button 
                      className="create_btn"
                      onClick={handleCreateList}
                      disabled={!newListName.trim() || creatingList}
                    >
                      {creatingList ? "Creating..." : "Create Group"}
                    </button>
                    <button 
                      className="cancel_btn"
                      onClick={() => setShowCreateForm(false)}
                      disabled={creatingList}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}
          
          <SimplifiedGroupsList />
        </div>

        {/* Footer Below */}
        {/* <div className="footer_bar">
          <div className="fb_copyright">
            <Link to="/privacy">Privacy</Link>
            <span>. </span>
            <Link to="/terms">Terms</Link>
            <span>. </span>
            <Link to="/cookies">Cookies</Link>
            <span>. </span> <br />
            UsmaApp © 2024
          </div>
        </div> */}
      </div>
    </div>
  );
}